import {
  Toaster as BlueprintToaster,
  Classes,
  OverlayToaster,
  OverlayToasterProps,
  Position,
  ToastOptions,
  ToastProps,
  ToasterPosition,
} from "@blueprintjs/core";
import React, {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";

export const ToastIcon = styled.div`
  padding: 0 0 0 15px;

  font-size: 24px;

  /*
  This hides the default close button (which doesn't seem disable-able) in any other way.
  I needed to center the button vertically and decided the only way to do that was either use the
  <Toast /> component and wrap that in css, or do this.
  */
  & ~ .${Classes.BUTTON_GROUP} {
    display: none;
  }
`;

export const ToastBody = styled.div`
  display: flex;
  align-items: center;

  background-color: ${({ theme }) => theme.backgroundColor.DEFAULT};
`;

export const ToastContent = styled.div`
  margin-right: 50px;
`;

export const ToastContentTitle = styled.div`
  margin-bottom: 2px;

  font-weight: ${({ theme }) => theme.fontWeight.MEDIUM};
`;

export const ToastContentDescription = styled.div`
  color: ${({ theme }) => theme.fontColor.MUTED};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
`;

export interface HexToastProps extends ToastProps {
  /**
   * Where to show the given toast
   *
   * @default Position.TOP
   */
  position?: ToasterPosition;
}

export interface HexToasterInstance extends OverlayToaster {
  show: (props: HexToastProps, key?: string) => string;
}

class LazyOverlayToaster implements BlueprintToaster {
  private pendingToasts = new Map<string, ToastProps>();
  private toaster: BlueprintToaster | null = null;
  private toasterPromise: Promise<BlueprintToaster> | null = null;

  private static nextToastId = 0;

  constructor(private position: ToasterPosition) {}

  private getToasterPromise(): Promise<BlueprintToaster> {
    this.toasterPromise ??= OverlayToaster.create({
      position: this.position,
    }).then((toaster) => {
      this.toaster = toaster;
      for (const [key, props] of this.pendingToasts) {
        toaster.show(props, key);
      }
      this.pendingToasts.clear();
      return toaster;
    });
    return this.toasterPromise;
  }

  public show(props: ToastProps, key?: string): string {
    const toastKey =
      key ?? `hex-default-toast-${LazyOverlayToaster.nextToastId++}`;
    if (this.toaster) {
      return this.toaster.show(props, toastKey);
    }
    this.pendingToasts.set(toastKey, props);
    void this.getToasterPromise();
    return toastKey;
  }

  public dismiss(key: string): void {
    this.pendingToasts.delete(key);
    this.toaster?.dismiss(key);
  }

  public clear(): void {
    this.pendingToasts.clear();
    this.toaster?.clear();
  }

  public getToasts(): ToastOptions[] {
    const pendingToasts = Array.from(this.pendingToasts, ([key, props]) => ({
      ...props,
      key,
    }));
    return [...(this.toaster?.getToasts() ?? []), ...pendingToasts];
  }
}

export class HexToaster {
  constructor(
    private toasters: {
      [K in ToasterPosition]: BlueprintToaster;
    },
  ) {}

  public show: HexToasterInstance["show"] = (
    { position = Position.TOP, ...props },
    key,
  ) => {
    return this.toasters[position].show(props, key);
  };

  public dismiss: HexToasterInstance["dismiss"] = (key: string) => {
    for (const toaster of Object.values(this.toasters)) {
      toaster.dismiss(key);
    }
  };

  public clear: HexToasterInstance["clear"] = (...args) => {
    for (const toaster of Object.values(this.toasters)) {
      toaster.clear(...args);
    }
  };

  public getToasts: HexToasterInstance["getToasts"] = (...args) => {
    const options: ToastOptions[] = [];
    for (const toaster of Object.values(this.toasters)) {
      options.push(...toaster.getToasts(...args));
    }
    return options;
  };
}

/* eslint-disable tree-shaking/no-side-effects-in-initialization */
const DefaultToaster = new HexToaster({
  [Position.TOP_LEFT]: new LazyOverlayToaster(Position.TOP_LEFT),
  [Position.TOP]: new LazyOverlayToaster(Position.TOP),
  [Position.TOP_RIGHT]: new LazyOverlayToaster(Position.TOP_RIGHT),
  [Position.BOTTOM_RIGHT]: new LazyOverlayToaster(Position.BOTTOM_RIGHT),
  [Position.BOTTOM]: new LazyOverlayToaster(Position.BOTTOM),
  [Position.BOTTOM_LEFT]: new LazyOverlayToaster(Position.BOTTOM_LEFT),
});
/* eslint-enable tree-shaking/no-side-effects-in-initialization */

const ToasterContext = createContext<HexToaster>(DefaultToaster);

export function useToaster(): HexToaster {
  return useContext(ToasterContext);
}

const PrivateToasterContext = createContext<
  | {
      setTopLeftToaster: (toaster: HexToasterInstance) => void;
      setTopToaster: (toaster: HexToasterInstance) => void;
      setTopRightToaster: (toaster: HexToasterInstance) => void;
      setBottomLeftToaster: (toaster: HexToasterInstance) => void;
      setBottomToaster: (toaster: HexToasterInstance) => void;
      setBottomRightToaster: (toaster: HexToasterInstance) => void;
    }
  | undefined
>(undefined);

const usePrivateToasterContext = () => {
  const context = useContext(PrivateToasterContext);
  if (!context) {
    throw new Error(
      "usePrivateToasterContext must be used within a ToasterProvider",
    );
  }
  return context;
};

export interface HexToasterProviderProps {
  children?: ReactNode;
}

export const HexToasterProvider: React.ComponentType<HexToasterProviderProps> =
  React.memo(function HexToasterProvider({ children }) {
    const [topLeftToaster, setTopLeftToaster] =
      useState<HexToasterInstance | null>(null);
    const [topToaster, setTopToaster] = useState<HexToasterInstance | null>(
      null,
    );
    const [topRightToaster, setTopRightToaster] =
      useState<HexToasterInstance | null>(null);
    const [bottomLeftToaster, setBottomLeftToaster] =
      useState<HexToasterInstance | null>(null);
    const [bottomToaster, setBottomToaster] =
      useState<HexToasterInstance | null>(null);
    const [bottomRightToaster, setBottomRightToaster] =
      useState<HexToasterInstance | null>(null);

    const toaster = useMemo(
      () =>
        topLeftToaster &&
        topToaster &&
        topRightToaster &&
        bottomLeftToaster &&
        bottomToaster &&
        bottomRightToaster
          ? new HexToaster({
              [Position.TOP_LEFT]: topLeftToaster,
              [Position.TOP]: topToaster,
              [Position.TOP_RIGHT]: topRightToaster,
              [Position.BOTTOM_LEFT]: bottomLeftToaster,
              [Position.BOTTOM]: bottomToaster,
              [Position.BOTTOM_RIGHT]: bottomRightToaster,
            })
          : undefined,
      [
        bottomLeftToaster,
        bottomRightToaster,
        bottomToaster,
        topLeftToaster,
        topRightToaster,
        topToaster,
      ],
    );

    const setters = useMemo(
      () => ({
        setTopLeftToaster,
        setTopToaster,
        setTopRightToaster,
        setBottomLeftToaster,
        setBottomToaster,
        setBottomRightToaster,
      }),
      [
        setTopLeftToaster,
        setTopToaster,
        setTopRightToaster,
        setBottomLeftToaster,
        setBottomToaster,
        setBottomRightToaster,
      ],
    );

    return (
      <ToasterContext value={toaster ?? DefaultToaster}>
        <PrivateToasterContext value={setters}>
          {children}
        </PrivateToasterContext>
      </ToasterContext>
    );
  });

interface HexToasterOverlaysProps extends Omit<
  OverlayToasterProps,
  "position"
> {}

export const HexToasterOverlays = React.memo(function HexToasterOverlays(
  props: HexToasterOverlaysProps,
) {
  const {
    setBottomLeftToaster,
    setBottomRightToaster,
    setBottomToaster,
    setTopLeftToaster,
    setTopRightToaster,
    setTopToaster,
  } = usePrivateToasterContext();

  return (
    <>
      <OverlayToaster
        {...props}
        ref={setTopLeftToaster}
        position={Position.TOP_LEFT}
      />
      <OverlayToaster {...props} ref={setTopToaster} position={Position.TOP} />
      <OverlayToaster
        {...props}
        ref={setTopRightToaster}
        position={Position.TOP_RIGHT}
      />
      <OverlayToaster
        {...props}
        ref={setBottomLeftToaster}
        position={Position.BOTTOM_LEFT}
      />
      <OverlayToaster
        {...props}
        ref={setBottomToaster}
        position={Position.BOTTOM}
      />
      <OverlayToaster
        {...props}
        ref={setBottomRightToaster}
        position={Position.BOTTOM_RIGHT}
      />
    </>
  );
});
